import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common/States";
import AppLayout from "@/components/layout/AppLayout";
import { ForgotPasswordPage, LoginPage, RegisterPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/auth/AuthPages";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Agenda from "@/pages/Agenda";
import Students from "@/pages/Students";
import StudentDetail from "@/pages/StudentDetail";
import Lessons from "@/pages/Lessons";
import Finance from "@/pages/Finance";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import { PaymentCancelPage, PaymentSuccessPage, PlansPage } from "@/pages/Plans";
import { AcceptInvitePage, PortalPage } from "@/pages/Portal";

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="app-loading"><Spinner className="h-6 w-6 text-primary" /></div>
);

const homeFor = (u) => (u.role === "admin" ? "/admin" : u.role === "student" ? "/portal" : u.onboarding_completed ? "/dashboard" : "/onboarding");

function RequireAuth({ role, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (role && user.role !== role) return <Navigate to={homeFor(user)} replace />;
  if (role === "teacher" && !user.onboarding_completed && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  return user ? <Navigate to={homeFor(user)} replace /> : children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

const teacher = (el) => <RequireAuth role="teacher">{el}</RequireAuth>;

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" storageKey="ph-theme" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />
            <Route path="/cadastro" element={<GuestOnly><RegisterPage /></GuestOnly>} />
            <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
            <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
            <Route path="/verificar-email" element={<VerifyEmailPage />} />
            <Route path="/planos" element={<PlansPage />} />
            <Route path="/onboarding" element={teacher(<Onboarding />)} />
            <Route path="/admin" element={<RequireAuth role="admin"><Admin /></RequireAuth>} />
            <Route path="/portal" element={<RequireAuth role="student"><PortalPage /></RequireAuth>} />
            <Route path="/convite" element={<GuestOnly><AcceptInvitePage /></GuestOnly>} />
            <Route element={teacher(<AppLayout />)}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/agenda" element={<Agenda />} />
              <Route path="/alunos" element={<Students />} />
              <Route path="/alunos/:id" element={<StudentDetail />} />
              <Route path="/aulas" element={<Lessons />} />
              <Route path="/financeiro" element={<Finance />} />
              <Route path="/relatorios" element={<Reports />} />
              <Route path="/configuracoes" element={<Settings />} />
              <Route path="/pagamento/sucesso" element={<PaymentSuccessPage />} />
              <Route path="/pagamento/cancelado" element={<PaymentCancelPage />} />
            </Route>
            <Route path="*" element={<Home />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}
